import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";
import {
  PreOrderFlowCreate,
  PreOrderFlowResponse,
  PreOrderFlowUpdate,
  PreOrderFlowWithProduct,
  StockHistoryFlowResponse,
} from "@/app/api/types/preOrderFlow";

export default class APIPreOrderFlows extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Create a new pre-order flow
   */
  createFlow = async (
    flowData: PreOrderFlowCreate,
  ): Promise<PreOrderFlowResponse> => {
    const res = await this._fetch({
      url: `/pre-order-flows/`,
      method: "POST",
      body: flowData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get a specific pre-order flow with product details
   */
  getFlow = async (flowId: UUID): Promise<PreOrderFlowWithProduct> => {
    const res = await this._fetch({
      url: `/pre-order-flows/${flowId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all flows for a specific pre-order
   */
  getFlowsByPreOrder = async (
    preOrderId: UUID,
  ): Promise<PreOrderFlowWithProduct[]> => {
    const res = await this._fetch({
      url: `/pre-order-flows/pre-order/${preOrderId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get all pre-order flows for the current company
   */
  getCompanyFlows = async (): Promise<PreOrderFlowResponse[]> => {
    const res = await this._fetch({
      url: `/pre-order-flows/company/all`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get stock history: pre-order flows for a product/location combination
   */
  getStockHistory = async (
    productId: UUID,
    locationId?: UUID | null,
    limit: number = 50,
  ): Promise<StockHistoryFlowResponse[]> => {
    const params: Record<string, unknown> = { product_id: productId, limit };
    if (locationId) params.location_id = locationId;
    const res = await this._fetch({
      url: `/pre-order-flows/stock-history`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all pre-order flows for a specific product
   */
  getFlowsByProduct = async (
    productId: UUID,
  ): Promise<PreOrderFlowResponse[]> => {
    const res = await this._fetch({
      url: `/pre-order-flows/product/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Update a pre-order flow
   */
  updateFlow = async (
    flowId: UUID,
    flowUpdate: PreOrderFlowUpdate,
  ): Promise<PreOrderFlowResponse> => {
    const res = await this._fetch({
      url: `/pre-order-flows/${flowId}`,
      method: "PUT",
      body: flowUpdate,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Delete a pre-order flow
   */
  deleteFlow = async (flowId: UUID): Promise<void> => {
    await this._fetch({
      url: `/pre-order-flows/${flowId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  /**
   * Reorder flows for a pre-order
   */
  reorderFlows = async (
    preOrderId: UUID,
    flowOrder: Record<string, number>,
  ): Promise<PreOrderFlowResponse[]> => {
    const res = await this._fetch({
      url: `/pre-order-flows/reorder/${preOrderId}`,
      method: "POST",
      body: flowOrder,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Add a new flow to an existing pre-order
   */
  addFlowToPreOrder = async (
    preOrderId: UUID,
    flowData: {
      product_id?: string | null;
      partner_id?: string | null;
      quantity: number;
      unit?: string | null;
      weight?: number | null;
      price_per_weight?: number | null;
      comment?: string | null;
      hidden_comment?: string | null;
      index?: number | null;
    },
  ): Promise<PreOrderFlowResponse> => {
    const res = await this._fetch({
      url: `/pre-order-flows/pre-order/${preOrderId}/add`,
      method: "POST",
      body: flowData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Confirm a flow (change status from PENDING to CONFIRMED)
   */
  confirmFlow = async (flowId: UUID): Promise<PreOrderFlowResponse> => {
    const res = await this._fetch({
      url: `/pre-order-flows/${flowId}/confirm`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Cancel a flow with optional reason
   */
  cancelFlow = async (
    flowId: UUID,
    request: { reason?: string },
  ): Promise<PreOrderFlowResponse> => {
    const res = await this._fetch({
      url: `/pre-order-flows/${flowId}/cancel`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Bulk update status for multiple flows
   */
  bulkUpdateStatus = async (data: {
    flow_ids: UUID[];
    status: number;
  }): Promise<{ updated_count: number }> => {
    const res = await this._fetch({
      url: `/pre-order-flows/bulk-update-status`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Batch validate flow changes (create, update, delete) in a single transaction
   */
  batchValidate = async (data: {
    changes: Array<{
      type: "create" | "update" | "delete";
      pre_order_id: string;
      flow_id?: string | null;
      flow_data?: PreOrderFlowCreate | PreOrderFlowUpdate | null;
    }>;
  }): Promise<{
    created: PreOrderFlowResponse[];
    updated: PreOrderFlowResponse[];
    deleted: string[];
    id_mapping: Record<string, string>;
  }> => {
    const res = await this._fetch({
      url: `/pre-order-flows/batch-validate`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
